/**
 * Patches globalThis.fetch before the generated MCP server is imported.
 *
 * Auth precedence (first match wins):
 *   1. AVALLON_ACCESS_TOKEN  → Bearer token
 *   2. AVALLON_API_KEY       → x-api-key header
 */

const DEFAULT_BASE_URL = "https://api.avallon.ai";

export function setupFetch(): void {
  const originalFetch = globalThis.fetch;

  globalThis.fetch = async function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const baseUrl = process.env.AVALLON_BASE_URL ?? DEFAULT_BASE_URL;

    // Resolve URL: prepend base URL for relative paths
    let url: string;
    if (typeof input === "string") {
      url = input.startsWith("http") ? input : `${baseUrl}${input}`;
    } else if (input instanceof URL) {
      url = input.href.startsWith("http")
        ? input.href
        : `${baseUrl}${input.href}`;
    } else {
      // Request object
      url = (input as Request).url;
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );

    // Only set Content-Type for requests with a JSON string body.
    // This lets multipart/form-data requests set their own Content-Type.
    const body =
      init?.body ?? (input instanceof Request ? input.body : undefined);
    if (body && typeof body === "string") {
      headers.set("Content-Type", "application/json");
    }

    const accessToken = process.env.AVALLON_ACCESS_TOKEN;
    const apiKey = process.env.AVALLON_API_KEY;

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    } else if (apiKey) {
      headers.set("x-api-key", apiKey);
    }

    const response = await originalFetch(url, { ...init, headers });

    if (!response.ok) {
      const contentType = response.headers.get("Content-Type") ?? "";
      let msg: unknown;
      if (contentType.includes("application/json")) {
        const errBody = await response.json().catch(() => ({}));
        msg =
          (errBody as Record<string, unknown>)?.message ??
          `API error ${response.status}`;
      } else {
        msg = await response.text().catch(() => `API error ${response.status}`);
      }
      throw new Error(`Avallon API error ${response.status}: ${String(msg)}`);
    }

    return response;
  };
}
